import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from '../../../app/LanguageContext';
import { MediaGallery } from '../../medias/public';
import {
  REGISTER_LINE_PRODUCT_IMAGE_KEY,
  REGISTER_LINE_SKU_IMAGE_KEY,
  buildRegisterLineMediaScope,
  cleanupRegisterLineItemCatalogMedias,
  seedRegisterLineItemCatalogMedias,
} from './lineItemCatalogMediaImport';

export default function RegisterLineItemCatalogMediaSection({
  open,
  orderId,
  order,
  formItems,
  formItem,
  disabled = false,
}) {
  const { translate } = useTranslation();
  const formItemId = formItem?.id;
  const scopeValue =
    orderId && formItemId ? buildRegisterLineMediaScope(orderId, formItemId) : '';
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const sessionKey = open
    ? `${scopeValue}-${String(formItem?.description ?? '').trim()}`
    : '';

  useEffect(() => {
    if (!open || !orderId || !formItemId) {
      setReady(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setReady(false);
    (async () => {
      try {
        const changed = await seedRegisterLineItemCatalogMedias({
          order,
          formItems,
          orderId,
          formItemId,
          formItem,
        });
        if (cancelled) return;
        if (changed) {
          setRefreshKey((k) => k + 1);
        }
      } catch {
        if (!cancelled) {
          setRefreshKey((k) => k + 1);
        }
      } finally {
        if (!cancelled) {
          setReady(true);
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, sessionKey, orderId, formItemId, order, formItems, formItem]);

  useEffect(() => {
    if (open || !orderId || !formItemId) {
      return undefined;
    }

    setReady(false);
    void cleanupRegisterLineItemCatalogMedias({ orderId, formItemId });

    return undefined;
  }, [open, orderId, formItemId]);

  if (!scopeValue) {
    return null;
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        {translate('orders.register_line_item_media_section')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {translate('orders.register_line_item_media_hint')}
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : null}

      {ready ? (
        <>
          <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
            {translate('products.image_gallery')}
          </Typography>
          <MediaGallery
            metadataKey={REGISTER_LINE_PRODUCT_IMAGE_KEY}
            metadataValue={scopeValue}
            accept="image/*"
            compact
            enablePaste={!disabled}
            readOnly={disabled}
            refreshKey={refreshKey}
          />

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            {translate('products.variant_image_section')}
          </Typography>
          <MediaGallery
            metadataKey={REGISTER_LINE_SKU_IMAGE_KEY}
            metadataValue={scopeValue}
            accept="image/*"
            compact
            enablePaste={!disabled}
            readOnly={disabled}
            refreshKey={refreshKey}
            selectExistingScope={{
              metadataKey: REGISTER_LINE_PRODUCT_IMAGE_KEY,
              metadataValue: scopeValue,
            }}
          />
        </>
      ) : null}
    </Box>
  );
}
